import { useCallback, useRef, useState } from 'react';
import type { CarouselHandle, ScrollState } from '../carousel/Carousel';

export enum Direction {
    LEFT,
    RIGHT,
}

export function usePaging(pageTotal: number, gridGap: number) {
    const [currentPage, setCurrentPage] = useState(0);
    const carouselRef = useRef<CarouselHandle>(null);

    const handlePagingClick = useCallback(
        (direction: Direction) => {
            const pageIncrement = direction === Direction.LEFT ? -1 : 1;
            const nextPage = Math.min(
                Math.max(currentPage + pageIncrement, 0),
                pageTotal - 1,
            );
            const scrollLeft =
                nextPage * (carouselRef.current?.scrollState.offsetWidth ?? 0);
            const scrollPadding = gridGap * nextPage;
            carouselRef.current?.scrollTo(
                scrollLeft === 0 ? 0 : scrollLeft + scrollPadding,
            );
        },
        [currentPage, gridGap, pageTotal],
    );

    const handleScroll = useCallback(
        (scrollState: ScrollState) => {
            const { scrollLeft, offsetWidth, scrollWidth } = scrollState;
            const isLastPage = scrollLeft + offsetWidth === scrollWidth;
            if (isLastPage) {
                setCurrentPage(pageTotal - 1);
            } else {
                const nextPage = Math.floor(
                    scrollLeft / (offsetWidth + gridGap),
                );
                setCurrentPage(nextPage);
            }
        },
        [gridGap, pageTotal],
    );

    return { carouselRef, currentPage, handlePagingClick, handleScroll };
}
